import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLang } from "../context/LanguageContext";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { StatusBadge } from "../components/common/StatusBadge";
import { EntityDialog } from "../components/common/EntityDialog";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { VOs, CLFs } from "../lib/api";
import { formatIndianDate } from "../lib/format";
import { toast } from "sonner";
import { Plus, Pencil, PowerOff, Power } from "lucide-react";

const VosPage = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [clfs, setClfs] = useState([]);
  const [clfFilter, setClfFilter] = useState("all");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vosData, clfsData] = await Promise.all([
        VOs.list({ include_inactive: includeInactive, ...(clfFilter !== "all" ? { clf_id: clfFilter } : {}) }),
        CLFs.list({}),
      ]);
      setRows(vosData);
      setClfs(clfsData);
    } catch (e) {
      toast.error(e.message || "Failed to load VOs");
    } finally {
      setLoading(false);
    }
  }, [includeInactive, clfFilter]);

  useEffect(() => { load(); }, [load]);

  const clfOptions = useMemo(
    () => clfs.map((c) => ({ value: c.id, label: `${c.name} (${c.registration_number})` })),
    [clfs],
  );
  const clfNameById = useMemo(() => {
    const m = {}; clfs.forEach((c) => { m[c.id] = c.name; }); return m;
  }, [clfs]);

  const fields = [
    { name: "clf_id", label: "Parent CLF", type: "select", required: true, options: clfOptions, placeholder: "Select CLF" },
    { name: "name", label: "VO name", required: true, placeholder: "e.g. Radhakishorepur Gramya VO" },
    { name: "code", label: "VO code", required: true, halfWidth: true, placeholder: "e.g. VO-TRP-101" },
    { name: "contact_person", label: "Contact person", halfWidth: true },
    { name: "phone", label: "Phone", type: "tel", halfWidth: true, placeholder: "+91…" },
    { name: "address", label: "Address", halfWidth: true },
  ];
  const editFields = [
    ...fields,
    { name: "status", label: "Status", type: "select", halfWidth: true, options: [
      { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" },
    ] },
  ];

  const handleCreate = async (v) => {
    await VOs.create(v);
    toast.success("VO created");
    await load();
  };
  const handleUpdate = async (v) => {
    await VOs.update(editing.id, v);
    toast.success("VO updated");
    await load();
  };
  const toggleStatus = async (row) => {
    if (row.status === "active") { await VOs.deactivate(row.id); toast.success(`${row.name} deactivated`); }
    else { await VOs.activate(row.id); toast.success(`${row.name} activated`); }
    await load();
  };

  const columns = [
    { key: "code", header: "Code", mono: true, strong: true },
    { key: "name", header: "VO name", strong: true },
    { key: "clf_id", header: "CLF", render: (r) => clfNameById[r.clf_id] || "—" },
    { key: "contact_person", header: "Contact" },
    { key: "phone", header: "Phone", mono: true },
    { key: "created_at", header: "Created", mono: true, render: (r) => formatIndianDate(r.created_at) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status === "active" ? "Active" : "Closed"} /> },
    { key: "_actions", header: "Actions", render: (r) => (
      <div className="flex items-center gap-1.5">
        <Button size="sm" variant="outline" data-testid={`vo-edit-${r.id}`}
          onClick={() => { setEditing(r); setDialogOpen(true); }} className="h-8 px-2">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="outline" data-testid={`vo-toggle-${r.id}`}
          onClick={() => setConfirm({ row: r, action: r.status === "active" ? "deactivate" : "activate" })}
          className={`h-8 px-2 ${r.status === "active" ? "border-rose-300 text-rose-700 hover:bg-rose-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
          {r.status === "active" ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
        </Button>
      </div>
    )},
  ];

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title={t.nav.vos} subtitle="Village Organizations under each CLF"
        testId="vos-page-header" showSampleBadge={false}
        actions={
          <>
            <Select value={clfFilter} onValueChange={setClfFilter}>
              <SelectTrigger className="h-9 w-56 bg-white" data-testid="vo-clf-filter">
                <SelectValue placeholder="Filter by CLF" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All CLFs</SelectItem>
                {clfs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-xs text-emerald-900/70">
              <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} data-testid="toggle-include-inactive" />
              Show inactive
            </label>
            <Button data-testid="vo-add-btn"
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              disabled={clfs.length === 0}
              className="bg-[#0F4C3A] text-white hover:bg-emerald-900">
              <Plus className="mr-1.5 h-4 w-4" />Add VO
            </Button>
          </>
        }
      />

      {clfs.length === 0 && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          Please register at least one CLF before adding Village Organizations.
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-emerald-950/10 bg-white p-10 text-center text-sm text-emerald-900/50">Loading…</div>
      ) : (
        <DataTable columns={columns} rows={rows}
          searchKeys={["name", "code", "contact_person", "phone"]}
          testId="vos-table"
          emptyLabel="No VOs yet. Click “Add VO” to register one under a CLF." />
      )}

      <EntityDialog
        open={dialogOpen} onOpenChange={setDialogOpen}
        title={editing ? "Edit VO" : "Register new VO"}
        fields={editing ? editFields : fields}
        initialValues={editing || { status: "active" }}
        onSubmit={editing ? handleUpdate : handleCreate}
        submitLabel={editing ? "Save changes" : "Create VO"}
        testIdPrefix="vo-dialog"
      />
      <ConfirmDialog
        open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.action === "deactivate" ? "Deactivate this VO?" : "Reactivate this VO?"}
        description={confirm?.action === "deactivate"
          ? `“${confirm?.row?.name}” will be hidden from active lists.`
          : `“${confirm?.row?.name}” will become active again.`}
        confirmLabel={confirm?.action === "deactivate" ? "Deactivate" : "Reactivate"}
        destructive={confirm?.action === "deactivate"}
        testId="vo-confirm"
        onConfirm={async () => { await toggleStatus(confirm.row); setConfirm(null); }}
      />
    </div>
  );
};

export default VosPage;
